/**
 * Boost-powered predictive search for the Focal side-drawer.
 *
 * Keeps Focal's native <predictive-search-drawer> exactly as-is — open/close, slide
 * animation, overlay, focus-trap, spinner, 200ms debounce, abort-on-retype and the
 * "View all results" footer — and only swaps the *data source*. Instead of Shopify's
 * native /search/suggest, results come from Boost AI Search & Discovery's Suggest API
 * and are rendered into Focal's own result markup so the drawer looks identical.
 *
 * Renders every result type the one Suggest call already returns: did-you-mean and term
 * suggestions above the products, collections and pages below. Non-product rows reuse
 * Focal's own .predictive-search__linklist markup (bordered rows + hover arrow + <mark>
 * match highlighting), so no new CSS is needed and the drawer keeps the theme's design.
 *
 * Hook: overrides PredictiveSearchDrawer.prototype._doPredictiveSearch and
 * _doLiquidSearch (defined in theme.js) via customElements.whenDefined, so theme.js is
 * never edited. Both return Focal's expected contract: { hasResults, html }, where
 * `html` is injected into `.predictive-search__results` and `hasResults` toggles the
 * footer. The "View all results" button still submits to /search?q=... which is the
 * Boost-powered results page (boost-sd-ssr), so the in-drawer and full results match.
 */
(function () {
  'use strict';

  var BOOST_ENDPOINT = 'https://services.mybcapps.com/bc-sf-filter/search/suggest';
  var PRODUCT_LIMIT = 10;
  var SUGGESTION_LIMIT = 4;
  var COLLECTION_LIMIT = 4;
  var PAGE_LIMIT = 4;

  // Focal has no translation for these; its own strings are used where one exists.
  // Boost's `pages` array mixes pages AND blog articles, so this matches the wording on
  // Boost's own results page ("Blogs & Pages") rather than Focal's narrower "Pages".
  var SUGGESTIONS_TITLE = 'Suggestions';
  var DID_YOU_MEAN_TITLE = 'Did you mean';
  var PAGES_TITLE = 'Blogs & Pages';

  // Exact nav-arrow-right icon as rendered by Focal's `icon` snippet (stroke-width from theme settings).
  var ARROW_SVG =
    '<svg focusable="false" width="17" height="14" class="icon icon--nav-arrow-right icon--direction-aware" viewBox="0 0 17 14">' +
    '<path d="M0 7h15M9 1l6 6-6 6" stroke="currentColor" stroke-width="1.4" fill="none"></path></svg>';

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Standard Shopify money formatter: cents -> string using the shop's money_format.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value;
    switch ((fmt.match(placeholder) || [])[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function moneyFormat() {
    var settings = window.themeVariables && window.themeVariables.settings;
    return (settings && settings.moneyFormat) || '${{amount}}';
  }

  // Boost returns prices in major units (e.g. 59.97); Shopify's formatter wants cents.
  function money(dollars) {
    var n = Number(dollars);
    if (isNaN(n)) return '';
    return formatMoney(Math.round(n * 100), moneyFormat());
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Append a Shopify CDN width param for a thumbnail (handles the existing ?v= query string).
  function sized(src, width) {
    if (!src) return '';
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  function showVendor() {
    var settings = window.themeVariables && window.themeVariables.settings;
    return !(settings && settings.showVendor === false);
  }

  function themeStrings() {
    return (window.themeVariables && window.themeVariables.strings) || {};
  }

  function absolute(path) {
    if (!path) return '';
    if (/^https?:/i.test(path)) return path;
    return rootUrl().replace(/\/$/, '') + (path.charAt(0) === '/' ? path : '/' + path);
  }

  function searchUrl(term) {
    return absolute('/search') + '?q=' + encodeURIComponent(term);
  }

  // Bold the matched substring, the way Focal styles <mark> in its own suggestion rows.
  function highlight(text, term) {
    var haystack = String(text == null ? '' : text);
    var needle = String(term == null ? '' : term);
    if (!needle) return escapeHtml(haystack);
    var at = haystack.toLowerCase().indexOf(needle.toLowerCase());
    if (at === -1) return escapeHtml(haystack);
    return escapeHtml(haystack.slice(0, at)) +
      '<mark>' + escapeHtml(haystack.slice(at, at + needle.length)) + '</mark>' +
      escapeHtml(haystack.slice(at + needle.length));
  }

  // One non-product row, in Focal's bordered linklist markup.
  function linkRow(href, labelHtml) {
    return (
      '<li class="predictive-search__linklist-item">' +
        '<a href="' + escapeHtml(href) + '" class="predictive-search__linklist-link" data-instant>' +
          '<span>' + labelHtml + '</span>' + ARROW_SVG +
        '</a>' +
      '</li>'
    );
  }

  function menuGroup(title, rowsHtml) {
    if (!rowsHtml) return '';
    return (
      '<div class="predictive-search__menu">' +
        '<p class="predictive-search__menu-title heading heading--small">' + escapeHtml(title) + '</p>' +
        '<ul class="predictive-search__linklist predictive-search__linklist--bordered list--unstyled" role="list">' +
          rowsHtml +
        '</ul>' +
      '</div>'
    );
  }

  function menuList(groupsHtml) {
    return groupsHtml ? '<div class="predictive-search__menu-list">' + groupsHtml + '</div>' : '';
  }

  // Boost returns suggestions and did_you_mean as plain strings; both link to a full search.
  function termsGroup(title, terms, limit, term) {
    var rows = (terms || []).slice(0, limit).map(function (entry) {
      var text = typeof entry === 'string' ? entry : (entry && (entry.suggestion || entry.title)) || '';
      return text ? linkRow(searchUrl(text), highlight(text, term)) : '';
    }).join('');
    return menuGroup(title, rows);
  }

  function collectionsGroup(collections) {
    var rows = (collections || []).slice(0, COLLECTION_LIMIT).map(function (collection) {
      if (!collection || !collection.handle) return '';
      return linkRow(absolute('/collections/' + collection.handle),
        escapeHtml(collection.title || collection.handle));
    }).join('');
    return menuGroup(themeStrings().searchCollections || 'Collections', rows);
  }

  function pagesGroup(pages) {
    var rows = (pages || []).slice(0, PAGE_LIMIT).map(function (page) {
      if (!page) return '';
      var path = page.url || (page.handle ? '/pages/' + page.handle : '');
      return path ? linkRow(absolute(path), escapeHtml(page.title || page.handle)) : '';
    }).join('');
    return menuGroup(PAGES_TITLE, rows);
  }

  /**
   * Focal's "View all results" button sits in the drawer footer, outside the HTML we return,
   * so the total is appended directly. Purely cosmetic — never allowed to break a search.
   */
  function updateViewAllLabel(drawer, total) {
    try {
      var scope = (drawer && drawer.querySelector) ? drawer : document;
      var button = scope.querySelector('.drawer__footer button[form="predictive-search-form"]');
      if (!button) return;
      if (!button.dataset.defaultLabel) button.dataset.defaultLabel = button.textContent.trim();
      var base = button.dataset.defaultLabel;
      button.textContent = (typeof total === 'number' && total > 0)
        ? base + ' (' + total.toLocaleString() + ')'
        : base;
    } catch (error) {
      /* label only */
    }
  }

  // Map one Boost product to Focal's predictive-search product-item markup.
  function productItem(product) {
    var href = rootUrl().replace(/\/$/, '') + '/products/' + product.handle;

    var info = (product.images_info && product.images_info[0]) || null;
    var rawSrc = info ? info.src : (product.images ? Object.values(product.images)[0] : '');
    var imageHtml = '';
    if (rawSrc) {
      var width = info && info.width ? ' width="' + info.width + '"' : '';
      var height = info && info.height ? ' height="' + info.height + '"' : '';
      var alt = escapeHtml((info && info.alt) || product.title || '');
      imageHtml =
        '<span class="line-item__image-wrapper">' +
        '<img class="line-item__image" alt="' + alt + '"' + width + height +
        ' src="' + escapeHtml(sized(rawSrc, 210)) + '"></span>';
    }

    var vendorHtml = (showVendor() && product.vendor)
      ? '<span class="product-item-meta__vendor heading heading--xxsmall">' + escapeHtml(product.vendor) + '</span>'
      : '';

    var price = Number(product.price_min);
    var compare = Number(product.compare_at_price_min);
    var priceHtml = (compare && compare > price)
      ? '<span class="price price--highlight">' + money(price) + '</span>' +
        '<span class="price price--compare">' + money(compare) + '</span>'
      : '<span class="price">' + money(price) + '</span>';

    return (
      '<li class="predictive-search__product-item line-item line-item--centered">' +
        '<a href="' + escapeHtml(href) + '" class="line-item__content-wrapper" data-instant>' +
          imageHtml +
          '<div class="line-item__info"><div class="product-item-meta">' +
            vendorHtml +
            '<span class="product-item-meta__title text--small">' + escapeHtml(product.title) + '</span>' +
            '<div class="product-item-meta__price-list-container text--small">' +
              '<div class="price-list">' + priceHtml + '</div>' +
            '</div>' +
          '</div></div>' +
          ARROW_SVG +
        '</a>' +
      '</li>'
    );
  }

  function productsHtml(products) {
    if (!products.length) return '';
    return (
      '<div class="predictive-search__results-categories">' +
        '<div class="predictive-search__results-categories-item" id="predictive-search-products">' +
          '<input type="hidden" form="predictive-search-form" name="type" value="product">' +
          '<ul class="predictive-search__product-list list--unstyled" role="list" data-type="products">' +
            products.map(productItem).join('') +
          '</ul>' +
        '</div>' +
      '</div>'
    );
  }

  function sideBlock(name, html) {
    return html
      ? '<div class="predictive-search__side predictive-search__side--' + name + '">' + html + '</div>'
      : '';
  }

  /**
   * Compose the drawer body. DOM order is terms -> collections -> products -> pages.
   * Phones render that order directly (custom.css collapses terms and collections into
   * horizontal chip rows, so products stay near the top); >=1000px grid-places all three
   * side blocks into one left column. Returns '' when Boost found nothing at all, which
   * the caller turns into Focal's no-results state.
   */
  function resultsHtml(data, term) {
    // On a typo Boost usually repeats the correction inside `suggestions` too, so drop
    // anything already shown as a did-you-mean rather than listing the term twice.
    var corrections = (data.did_you_mean || []).filter(Boolean);
    var shown = corrections.map(function (entry) { return String(entry).toLowerCase(); });
    var suggestions = (data.suggestions || []).filter(function (entry) {
      var text = typeof entry === 'string' ? entry : (entry && (entry.suggestion || entry.title)) || '';
      return text && shown.indexOf(String(text).toLowerCase()) === -1;
    });

    var terms = menuList(
      termsGroup(DID_YOU_MEAN_TITLE, corrections, SUGGESTION_LIMIT, term) +
      termsGroup(SUGGESTIONS_TITLE, suggestions, SUGGESTION_LIMIT, term)
    );
    var collections = menuList(collectionsGroup(data.collections));
    var pages = menuList(pagesGroup(data.pages));
    var products = productsHtml(data.products || []);
    var side = terms || collections || pages;
    if (!side && !products) return '';

    return (
      '<div class="predictive-search__split' + (side ? '' : ' predictive-search__split--no-side') + '">' +
        sideBlock('terms', terms) +
        sideBlock('collections', collections) +
        '<div class="predictive-search__primary">' + products + '</div>' +
        sideBlock('pages', pages) +
      '</div>'
    );
  }

  function noResultsHtml() {
    var strings = (window.themeVariables && window.themeVariables.strings) || {};
    var noResults = strings.searchNoResults || 'No results found';
    var newSearch = strings.searchNewSearch || 'New search';
    return (
      '<p class="text--large">' + escapeHtml(noResults) + '</p>' +
      '<div class="button-wrapper">' +
        '<button type="button" data-action="reset-search" class="button button--primary">' + escapeHtml(newSearch) + '</button>' +
      '</div>'
    );
  }

  /**
   * Replacement for Focal's fetch methods. Runs with `this` = the drawer element, so it
   * reuses Focal's AbortController (rapid typing cancels stale requests). Returns the same
   * { hasResults, html } contract Focal's _onSearch expects.
   */
  async function boostSearch(term) {
    var shop = window.Shopify && window.Shopify.shop;
    var url = BOOST_ENDPOINT +
      '?shop=' + encodeURIComponent(shop) +
      '&q=' + encodeURIComponent(term) +
      '&product_limit=' + PRODUCT_LIMIT +
      '&suggestion_limit=' + SUGGESTION_LIMIT +
      '&collection_limit=' + COLLECTION_LIMIT +
      '&page_limit=' + PAGE_LIMIT;

    try {
      var response = await fetch(url, {
        signal: this.abortController ? this.abortController.signal : undefined,
        headers: { Accept: 'application/json' }
      });
      var data = (await response.json()) || {};
      var html = resultsHtml(data, term);
      updateViewAllLabel(this, data.total_product);
      if (!html) {
        return { hasResults: false, html: noResultsHtml() };
      }
      return { hasResults: true, html: html };
    } catch (error) {
      // Let Focal's _onSearch handle the abort silently; degrade gracefully otherwise.
      if (error && error.name === 'AbortError') throw error;
      return { hasResults: false, html: noResultsHtml() };
    }
  }

  customElements.whenDefined('predictive-search-drawer').then(function (Drawer) {
    Drawer.prototype._doPredictiveSearch = boostSearch;
    Drawer.prototype._doLiquidSearch = boostSearch;
  });
})();
